import { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation } from '@/lib/api-hooks';
import { authClient } from '@/lib/auth-client';
import { backendApi } from '@/lib/backend';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { HeaderBrandLogo } from '@/components/branding/HeaderBrandLogo';

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function getAuthErrorMessage(error: unknown) {
  const authError = error as { status?: number; code?: string; message?: string } | null;
  if (authError?.status === 429) {
    return 'Too many sign-in attempts. Wait a minute and try again.';
  }
  if (authError?.code === 'INVALID_EMAIL_OR_PASSWORD' || authError?.status === 401) {
    return 'Incorrect email or password';
  }
  return (
    authError?.message ||
    (error as { error?: { message?: string } } | null)?.error?.message ||
    'Unable to sign in'
  );
}

export function AppLoginForm() {
  const seedAdmin = useMutation(backendApi.tenancy.seedAdmin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const signIn = () => authClient.signIn.email({
        email: normalizedEmail,
        password,
        rememberMe: true,
      });

      const firstAttempt = await signIn();
      if (!firstAttempt.error) {
        toast.success('Signed in');
        return;
      }

      // Only the env-configured admin can be (re)provisioned here; anyone else
      // would just spend a second rate-limited attempt on the same password.
      let seeded: { isEnvAdmin?: boolean } | null = null;
      try {
        seeded = await seedAdmin({ email: normalizedEmail });
      } catch {
        // Fall through to the original sign-in error.
      }
      if (!seeded?.isEnvAdmin) {
        throw new Error(getAuthErrorMessage(firstAttempt.error));
      }

      const secondAttempt = await signIn();
      if (secondAttempt.error) {
        throw new Error(getAuthErrorMessage(secondAttempt.error));
      }

      toast.success('Signed in');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to sign in'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <HeaderBrandLogo size="lg" />
          </div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Sign in with your assigned account
          </p>
        </div>

        <form onSubmit={handleSignIn} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@example.com"
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <Button type="submit" className="w-full" disabled={loading || !email || !password}>
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
